import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { Button } from '../../ui/button';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITIES = [
  { value: 'high', label: 'High Priority' },
  { value: 'medium', label: 'Medium Priority' },
  { value: 'low', label: 'Low Priority' }
];

const ENTITY_TYPES = [
  { value: 'client', label: 'Client' },
  { value: 'lead', label: 'Lead' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'contact', label: 'Contact' }
];

const CreateTaskModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    related_to_type: 'client',
    related_to_id: '',
    priority: 'medium',
    due_date: '',
    assigned_to: '',
    tags: ''
  });
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (isOpen) {
      fetchEntities(formData.related_to_type);
    }
  }, [isOpen, formData.related_to_type]);

  const fetchEntities = async (entityType) => {
    try {
      setLoading(true);
      let endpoint = '';
      
      switch (entityType) {
        case 'client':
          endpoint = '/api/crm/clients';
          break;
        case 'lead':
          endpoint = '/api/crm/leads';
          break;
        case 'opportunity':
          endpoint = '/api/crm/opportunities';
          break;
        case 'contact':
          endpoint = '/api/crm/contacts';
          break;
        default:
          return;
      }

      const response = await fetch(`${API_URL}${endpoint}?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch entities');

      const data = await response.json();
      setEntities(data);
      
      // Auto-select first entity if available
      if (data.length > 0) {
        setFormData(prev => ({ ...prev, related_to_id: data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching entities:', error);
      toast({
        title: 'Error',
        description: 'Failed to load entities',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.subject.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Task subject is required',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.related_to_id) {
      toast({
        title: 'Validation Error',
        description: 'Please select an entity',
        variant: 'destructive'
      });
      return;
    }

    try {
      setCreating(true);

      // Parse tags
      const tagsArray = formData.tags
        ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        : [];

      const payload = {
        type: 'task',
        subject: formData.subject.trim(),
        description: formData.description.trim() || null,
        related_to_type: formData.related_to_type,
        related_to_id: formData.related_to_id,
        priority: formData.priority,
        due_date: formData.due_date || null,
        assigned_to: formData.assigned_to.trim() || null,
        tags: tagsArray,
        completed: false
      };

      const response = await fetch(`${API_URL}/api/crm/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create task');
      }

      toast({
        title: 'Success',
        description: 'Task created successfully'
      });

      // Reset form
      setFormData({
        subject: '',
        description: '',
        related_to_type: 'client',
        related_to_id: '',
        priority: 'medium',
        due_date: '',
        assigned_to: '',
        tags: ''
      });

      onSuccess();
    } catch (error) {
      console.error('Error creating task:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create task',
        variant: 'destructive'
      });
    } finally {
      setCreating(false);
    }
  };

  const getEntityLabel = (entity) => {
    if (formData.related_to_type === 'client') {
      return `${entity.company_name} - ${entity.contact_person}`;
    } else if (formData.related_to_type === 'lead' || formData.related_to_type === 'opportunity') {
      return entity.name || entity.company || entity.id;
    } else if (formData.related_to_type === 'contact') {
      return `${entity.name} - ${entity.email || entity.phone || ''}`;
    }
    return entity.id;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-[#2B2D42]">Create New Task</h2>
          <button
            onClick={onClose}
            className="text-[#6C757D] hover:text-[#2B2D42]"
            disabled={creating}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Task Subject *
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="e.g., Follow up with client"
              required
              disabled={creating}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="Additional details about this task..."
              disabled={creating}
            />
          </div>

          {/* Entity Type Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Related To *
            </label>
            <select
              value={formData.related_to_type}
              onChange={(e) => {
                setFormData({ ...formData, related_to_type: e.target.value, related_to_id: '' });
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              required
              disabled={creating}
            >
              {ENTITY_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Entity Selection */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Select {ENTITY_TYPES.find(t => t.value === formData.related_to_type)?.label} *
            </label>
            {loading ? (
              <p className="text-sm text-[#6C757D]">Loading...</p>
            ) : entities.length === 0 ? (
              <p className="text-sm text-red-600">No {formData.related_to_type}s found. Please create one first.</p>
            ) : (
              <select
                value={formData.related_to_id}
                onChange={(e) => setFormData({ ...formData, related_to_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                required
                disabled={creating}
              >
                <option value="">Select {formData.related_to_type}</option>
                {entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {getEntityLabel(entity)}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Priority and Due Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                disabled={creating}
              >
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Due Date
              </label>
              <input
                type="datetime-local"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
                disabled={creating}
              />
            </div>
          </div>

          {/* Assigned To */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Assigned To
            </label>
            <input
              type="text"
              value={formData.assigned_to}
              onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="e.g., john.doe@company.com"
              disabled={creating}
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Tags
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              placeholder="e.g., urgent, follow-up (comma separated)"
              disabled={creating}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#00B4D8] hover:bg-[#0096B8]"
              disabled={creating}
            >
              <Plus className="h-4 w-4 mr-2" />
              {creating ? 'Creating...' : 'Create Task'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTaskModal;
